import { CalculationProgress, CalculationStartResult, PromoteConceptCalculationResult, StartCalculationRequest } from '@/lib/types/calculation';
import { fetchAuthenticated } from '@/lib/utilities';
import { UseMutationResult, UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const QUERY_KEY = ['calculation'];
const BASE_URL = `${import.meta.env.VITE_CALCULATION_API_URL}`;

export const useGetOngoingCalculations = (): UseQueryResult<CalculationStartResult, Error> =>
  useQuery({
    queryKey: [QUERY_KEY, 'ongoing'],
    queryFn: () => fetchAuthenticated<CalculationStartResult>(`${BASE_URL}/ongoing`),
  });

export const useGetCalculationProgress = (id: string | undefined): UseQueryResult<CalculationProgress, Error> =>
  useQuery({
    queryKey: ['progress'],
    queryFn: () => fetchAuthenticated<CalculationProgress>(`${BASE_URL}/progress/${id}`),
    refetchInterval: res => (res.state.data?.progress == 100 ? false : 1500),
    enabled: !!id,
  });

export const useGetConceptCalculationYears = (): UseQueryResult<number[], Error> =>
  useQuery({
    queryKey: [...QUERY_KEY, 'concepts'],
    queryFn: () => fetchAuthenticated<number[]>(`${BASE_URL}/calculate/concepts`),
  });

export const usePromoteConceptCalculation = (): UseMutationResult<PromoteConceptCalculationResult, Error, number> => {
  const queryClient = useQueryClient();

  return useMutation<PromoteConceptCalculationResult, Error, number>({
    mutationFn: (registratiejaar: number) =>
      fetchAuthenticated<PromoteConceptCalculationResult>(`${BASE_URL}/calculate/concepts/${registratiejaar}/promote`, {
        method: 'POST',
      }),
    mutationKey: [...QUERY_KEY, 'concepts', 'promote'],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, 'concepts'] });
    },
  });
};

export const useStartCalculationProcess = (): UseMutationResult<CalculationStartResult, Error, StartCalculationRequest> => {
  const queryClient = useQueryClient();

  return useMutation<CalculationStartResult, Error, StartCalculationRequest>({
    mutationFn: (request: StartCalculationRequest) =>
      fetchAuthenticated<CalculationStartResult>(`${BASE_URL}/calculate`, {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    mutationKey: [...QUERY_KEY, 'calculate'],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, 'calculate'] });
    },
  });
};
